import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import styled, { keyframes } from 'styled-components/macro';
import Swiper from 'react-id-swiper';
import 'swiper/css/swiper.css';
import { useTranslation } from 'react-i18next';
import { formatPrice } from 'Utils/tools';
import {
  BasketBarSelectedProducts,
  BasketBarSelectedProducts_selectedProducts,
} from './__generated__/BasketBarSelectedProducts';

export const GET_SELECTED_PRODUCT = gql`
  query BasketBarSelectedProducts {
    selectedProducts {
      id
      groupId
      title
      sellPrice
      publicPrice
      discountPercentage
      saleId
      imagesDatas {
        urlListPage
      }
      brandDatas {
        name
      }
      sizeDatas {
        productId
        size
        stock
        maxQuantity
      }
    }
  }
`;

/* ── Styled ── */

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1.4rem 0 0.8rem;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
`;

const SectionTitle = styled.div`
  padding: 0 1.6rem;
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 600;
  color: #000;
  text-transform: uppercase;
`;

const SwiperWrapper = styled.div`
  padding-left: 1.6rem;
  overflow: hidden;
  width: 100%;
  box-sizing: border-box;
  position: relative;

  .swiper-container {
    overflow: visible;
    width: 100%;
  }

  .swiper-button-prev,
  .swiper-button-next {
    width: 3.2rem;
    height: 3.2rem;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    top: 38%;

    &::after {
      font-size: 1.2rem;
      font-weight: 700;
      color: #000;
    }
  }

  .swiper-button-prev {
    left: 0;
  }

  .swiper-button-next {
    right: 0.8rem;
  }
`;

const SlideCard = styled.div`
  width: 16rem !important;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  cursor: pointer;
`;

const CardImageWrapper = styled.div`
  width: 100%;
  aspect-ratio: 3 / 4;
  overflow: hidden;
  border-radius: 4px;
  background: #f0f0f0;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transition: transform 0.3s ease;
  }

  &:hover img {
    transform: scale(1.04);
  }
`;

const CardBrand = styled.div`
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 700;
  color: #000;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardTitle = styled.div`
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 400;
  color: #777;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardPriceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex-wrap: wrap;
`;

const CardPrice = styled.div`
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 600;
  color: #000;
`;

const CardPublicPrice = styled.div`
  font-family: var(--font-primary);
  font-size: 1.4rem;
  font-weight: 400;
  color: #aaa;
  text-decoration: line-through;
`;

const CardDiscount = styled.div`
  font-family: var(--font-primary);
  font-size: 1.2rem;
  font-weight: 600;
  color: #000;
  background: linear-gradient(
    90deg,
    rgb(237, 237, 237) 0%,
    rgb(247, 247, 247) 100%
  );
  padding: 2px 6px;
  border-radius: 2px;
`;

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 1.4rem;
  height: 1.4rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  flex-shrink: 0;
`;

const AddButton = styled.button<{ $isLoading?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 1rem 0.4rem;
  background: var(--design-base-color, #000);
  color: #fff;
  font-family: var(--font-primary);
  font-size: 1.2rem;
  font-weight: 700;
  text-transform: uppercase;

  border: none;
  border-radius: 20px;
  cursor: ${({ $isLoading }) => ($isLoading ? 'default' : 'pointer')};
  opacity: ${({ disabled, $isLoading }) => (disabled && !$isLoading ? 0.5 : 1)};

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

const swiperParams = {
  spaceBetween: 24,
  slidesPerView: 'auto' as const,
  slidesPerGroup: 1,
  freeMode: true,
  grabCursor: true,
  simulateTouch: true,
  touchRatio: 1,
  mousewheel: {
    forceToAxis: true,
  },
  navigation: {
    nextEl: '.swiper-button-next',
    prevEl: '.swiper-button-prev',
  },
};

type Props = {
  onPickerOpen: (product: BasketBarSelectedProducts_selectedProducts) => void;
  addingGroupId: number | null;
};

export default function BasketBarCrossSell({
  onPickerOpen,
  addingGroupId,
}: Props) {
  const { t } = useTranslation('shipping', { useSuspense: false });
  const { data } = useQuery<BasketBarSelectedProducts>(GET_SELECTED_PRODUCT);
  const products = data?.selectedProducts ?? [];

  if (!products.length) return null;

  return (
    <Container>
      <SectionTitle>{t('crossSellTitle')}</SectionTitle>
      <SwiperWrapper>
        <Swiper {...swiperParams}>
          {products.map((product, index) => {
            const img = product?.imagesDatas?.[0]?.urlListPage;
            const isThisAdding =
              addingGroupId !== null && addingGroupId === product.groupId;

            return (
              <SlideCard key={product?.groupId ?? index}>
                <a
                  href={`/ventes/produit/id/${product?.id}/idcat/${product?.saleId}`}
                >
                  <CardImageWrapper>
                    {img && (
                      <img
                        src={img}
                        alt={product?.title ?? ''}
                        loading="lazy"
                      />
                    )}
                  </CardImageWrapper>
                </a>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                  }}
                >
                  <CardBrand>{product?.brandDatas?.name}</CardBrand>
                  <CardTitle>{product?.title}</CardTitle>
                  <CardPriceRow>
                    {product?.sellPrice != null && (
                      <CardPrice>{formatPrice(product.sellPrice)}</CardPrice>
                    )}
                    {product?.publicPrice != null &&
                      product.publicPrice > 0 &&
                      product.publicPrice !== product.sellPrice && (
                        <CardPublicPrice>
                          {formatPrice(product.publicPrice)}
                        </CardPublicPrice>
                      )}
                    {product?.discountPercentage != null &&
                      product.discountPercentage > 0 && (
                        <CardDiscount>
                          -{product.discountPercentage}%
                        </CardDiscount>
                      )}
                  </CardPriceRow>
                </div>
                <AddButton
                  disabled={isThisAdding}
                  $isLoading={isThisAdding}
                  onClick={() => onPickerOpen(product)}
                >
                  {isThisAdding ? <Spinner /> : t('addToBasket')}
                </AddButton>
              </SlideCard>
            );
          })}
        </Swiper>
      </SwiperWrapper>
    </Container>
  );
}
